/**
 * Decides between the first-data screen and the screen it stands in front of.
 *
 * A campaign has produced nothing when the overview's whole-life `posts`
 * figure reads zero (`owner-decisions-fe-data-plane-first-data-signal`,
 * option A). `null` means not measured, never zero (absence is never zero,
 * platform-design §5.4), so it leaves the data screen in place exactly like
 * a still-loading or failed read, which has its own loading and error
 * states.
 */

import { useState, type ReactNode } from 'react';

import { useOrgContext } from '../../session/orgContext';
import { useOverview } from '../api/queries';
import { FirstData } from './FirstData';

const CAPABILITY_WRITE = 'control_plane.write';

/** Before any campaign existed. */
const BEGINNING = '1970-01-01T00:00:00.000Z';

export function FirstDataGate(props: {
  campaignId: string;
  tenantId: string;
  children: ReactNode;
}): ReactNode {
  const context = useOrgContext();
  // Fixed at mount: a bound recomputed on every render is a new query key each time.
  const [window] = useState(() => ({ from: BEGINNING, to: new Date().toISOString() }));
  const overview = useOverview(props.campaignId, props.tenantId, { window });
  const collected = overview.data?.posts.value;

  if (
    collected === undefined ||
    collected === null ||
    collected > 0 ||
    context.kind !== 'campaign'
  ) {
    return props.children;
  }
  return (
    <FirstData
      campaignId={props.campaignId}
      tenantId={props.tenantId}
      campaignName={context.campaign.campaign_name}
      canWrite={context.capabilities.includes(CAPABILITY_WRITE)}
    />
  );
}
